import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArticleView } from "@/pages/HelpCenter";
import { toast } from "sonner";
import { HelpCircle, Flag } from "lucide-react";

const ROUTE_MODULE = {
  "/": "general", "/clients": "clients", "/policies": "policies", "/renewals": "renewals",
  "/claims": "claims", "/certificates": "certificates", "/service-cases": "service_cases",
  "/documents": "documents", "/forms": "forms", "/imports": "imports", "/reports": "reports",
  "/administration": "administration", "/conflicts": "conflicts", "/errors": "errors", "/email": "email",
  "/marketing": "marketing",
};

export function HelpButton() {
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [article, setArticle] = useState(null);
  const [report, setReport] = useState(false);

  const moduleKey = ROUTE_MODULE[Object.keys(ROUTE_MODULE).filter((k) => k !== "/" && loc.pathname.startsWith(k))[0]] || "general";

  useEffect(() => {
    if (open) api.get(`/help/context/${moduleKey}`).then(({ data }) => setArticle(data.article)).catch(() => setArticle(null));
  }, [open, moduleKey]);

  return (
    <>
      <button data-testid="help-button" onClick={() => setOpen(true)} className="relative p-2 rounded-sm hover:bg-ice nav-link" title="Help">
        <HelpCircle className="h-5 w-5 text-slate" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto bg-white" data-testid="help-drawer">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-navy">Help for This Page</DialogTitle>
            <DialogDescription className="text-xs text-mutedsea">Contextual guidance for the current module.</DialogDescription></DialogHeader>
          {article ? <ArticleView a={article} /> : <div className="text-sm text-mutedsea p-6 text-center">No specific help for this page yet.</div>}
          <DialogFooter className="gap-2">
            <Button variant="outline" data-testid="help-report-open" onClick={() => { setOpen(false); setReport(true); }} className="border-seared text-seared"><Flag className="h-4 w-4 mr-1.5" /> Report a Problem</Button>
            <Button data-testid="help-open-center" onClick={() => { setOpen(false); nav("/help"); }} className="bg-navy hover:bg-atlantic text-white">Open Help Center</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ReportProblem open={report} onOpenChange={setReport} page={loc.pathname} />
    </>
  );
}

export function ReportProblem({ open, onOpenChange, page }) {
  const [f, setF] = useState({ what_trying: "", description: "", severity: "MEDIUM" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ what_trying: "", description: "", severity: "MEDIUM" }); }, [open]);
  const submit = async () => {
    if (!f.description.trim()) { toast.error("Please describe the problem."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/errors/report", { ...f, page });
      toast.success(`Reported. Reference ${data.error_id}. Our team will review it.`);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not submit report."); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white" data-testid="report-problem-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-navy">Report a Problem</DialogTitle>
          <DialogDescription className="text-xs text-mutedsea">This creates a tracked entry in the Error Center with a reference ID.</DialogDescription></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-xs mb-1 block">What were you trying to do?</Label><Input data-testid="report-what" value={f.what_trying} onChange={(e) => setF({ ...f, what_trying: e.target.value })} className="bg-white text-sm" /></div>
          <div><Label className="text-xs mb-1 block">What happened? *</Label><Textarea data-testid="report-description" rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="bg-white text-sm" /></div>
          <div><Label className="text-xs mb-1 block">Severity</Label>
            <Select value={f.severity} onValueChange={(v) => setF({ ...f, severity: v })}><SelectTrigger data-testid="report-severity" className="bg-white text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="report-submit" onClick={submit} disabled={busy} className="bg-navy hover:bg-atlantic text-white">{busy ? "Submitting…" : "Submit Report"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
